import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { API } from "aws-amplify";
import { createLLMModel, updateLLMModel } from "../../graphql/mutations";
import { listLLMModels } from "../../graphql/queries";
import { CreateLLMModelInput, UpdateLLMModelInput } from "../../API";

interface ModelRecord {
  id: string;
  status: string;
  vendor: string;
  host: string;
  supported: boolean;
  name: string;
  description?: string | null;
  modelKey?: string | null;
  modelKeyLatest?: string | null;
  inputTokenPrice?: number | null;
  cachedTokenPrice?: number | null;
  outputTokenPrice?: number | null;  inputTokenBatchPrice?: number | null;
  outputTokenBatchPrice?: number | null;
  searchCost?: number | null;
  supportsSearch?: boolean | null;
  textInputSupport: boolean;
  imageInputSupport: boolean;
  audioInputSupport: boolean;
  textOutputSupport: boolean;
  imageOutputSupport: boolean;
  audioOutputSupport: boolean;
  thinkingSupport: boolean;
  contextWindowTokens?: number | null;
  maxResponseTokens?: number | null;  knowledgeCutoffDate?: string | null;
  lastCheckedDate?: string | null;
  supportedLanguages?: string | null;
  warningDate?: string | null;
  warningMessage?: string | null;
  deprecationDate?: string | null;
  deprecationMessage?: string | null;
  defaultServiceGroup?: string | null;
  defaultEnabled?: boolean | null;
  displayOrder?: number | null;
  recommendedUseCase?: string | null;
  performanceRating?: string | null;
}

// Interface for editing state - keeps price fields as strings to avoid input issues
interface EditingModelRecord extends Omit<ModelRecord, 'inputTokenPrice' | 'cachedTokenPrice' | 'outputTokenPrice' | 'inputTokenBatchPrice' | 'outputTokenBatchPrice' | 'searchCost'> {
  inputTokenPrice: string;
  cachedTokenPrice: string;
  outputTokenPrice: string;
  inputTokenBatchPrice: string;
  outputTokenBatchPrice: string;
  searchCost: string;
}

// Convert ModelRecord to EditingModelRecord for editing
const modelToEditingModel = (model: ModelRecord): EditingModelRecord => ({
  ...model,
  inputTokenPrice: model.inputTokenPrice?.toString() || "",
  cachedTokenPrice: model.cachedTokenPrice?.toString() || "",
  outputTokenPrice: model.outputTokenPrice?.toString() || "",
  inputTokenBatchPrice: model.inputTokenBatchPrice?.toString() || "",
  outputTokenBatchPrice: model.outputTokenBatchPrice?.toString() || "",
  searchCost: model.searchCost?.toString() || "",
});

// Convert string to number, handling empty strings and invalid values
const parseNumberOrUndefined = (value: string): number | undefined => {
  if (!value || value.trim() === "") return undefined;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
};

export default function AdminModels() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [models, setModels] = useState<ModelRecord[]>([]);
  const [selectedModel, setSelectedModel] = useState<EditingModelRecord | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [filterVendor, setFilterVendor] = useState("");
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStatusFilters, setExportStatusFilters] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    // Check for URL parameters to auto-open model for editing
    const urlParams = new URLSearchParams(window.location.search);
    const editModelId = urlParams.get('edit');
    
    if (editModelId && models.length > 0) {
      const modelToEdit = models.find(model => model.id === editModelId);
      if (modelToEdit) {
        handleEditClick(modelToEdit);
        // Clear the URL parameter after opening the dialog
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [models]); // Depend on models so this runs after models are loaded
  // Initialize export status filters when models change
  useEffect(() => {
    if (models.length > 0) {
      const uniqueStatuses = Array.from(new Set(models.map(model => model.status)));
      const initialFilters: Record<string, boolean> = {};
      uniqueStatuses.forEach(status => {
        initialFilters[status] = true; // Default to all selected
      });
      setExportStatusFilters(initialFilters);
    }
  }, [models]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const result = (await API.graphql({ query: listLLMModels })) as {
        data: {
          listLLMModels: {
            items: ModelRecord[];
          };
        };
      };
      setModels(result.data.listLLMModels.items);
    } catch (err) {
      setError("Error fetching models: " + String(err));
    }
    setLoading(false);
  };  const handleEditClick = (model: ModelRecord) => {
    setSelectedModel(modelToEditingModel(model));
    setIsCreatingNew(false);
    setOpenEditDialog(true);
  };
  const handleAddClick = () => {
    // Create a new model with default values
    const newModel: ModelRecord = {
      id: '', // Will be generated by the backend
      status: 'active',
      vendor: '',
      host: '',
      supported: true,
      name: '',
      description: '',
      modelKey: '',
      modelKeyLatest: '',
      inputTokenPrice: 0,
      cachedTokenPrice: 0,
      outputTokenPrice: 0,
      inputTokenBatchPrice: 0,
      outputTokenBatchPrice: 0,      searchCost: 0,
      supportsSearch: false,
      textInputSupport: true,
      imageInputSupport: false,
      audioInputSupport: false,
      textOutputSupport: true,
      imageOutputSupport: false,
      audioOutputSupport: false,
      thinkingSupport: false,
      contextWindowTokens: 0,
      maxResponseTokens: 0,      knowledgeCutoffDate: '',
      lastCheckedDate: '',
      supportedLanguages: '',
      warningDate: '',
      warningMessage: '',
      deprecationDate: '',
      deprecationMessage: '',
      defaultServiceGroup: 'Standard',
      defaultEnabled: true,
      displayOrder: 0,
      recommendedUseCase: '',
      performanceRating: 'Balanced',
    };
    setSelectedModel(modelToEditingModel(newModel));
    setIsCreatingNew(true);
    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!selectedModel) {
      console.log("No selected model");
      return;
    }

    console.log("Saving model:", selectedModel);
    console.log("Is creating new:", isCreatingNew);

    try {      if (isCreatingNew) {
        // Create new model
        const createLLMModelInput: CreateLLMModelInput = {
          status: selectedModel.status,
          vendor: selectedModel.vendor,
          host: selectedModel.host,
          supported: selectedModel.supported,
          name: selectedModel.name,
          description: selectedModel.description || undefined,
          modelKey: selectedModel.modelKey || undefined,
          modelKeyLatest: selectedModel.modelKeyLatest || undefined,
          inputTokenPrice: parseNumberOrUndefined(selectedModel.inputTokenPrice),
          cachedTokenPrice: parseNumberOrUndefined(selectedModel.cachedTokenPrice),
          outputTokenPrice: parseNumberOrUndefined(selectedModel.outputTokenPrice),
          inputTokenBatchPrice: parseNumberOrUndefined(selectedModel.inputTokenBatchPrice),
          outputTokenBatchPrice: parseNumberOrUndefined(selectedModel.outputTokenBatchPrice),
          searchCost: parseNumberOrUndefined(selectedModel.searchCost),
          supportsSearch: selectedModel.supportsSearch,
          textInputSupport: selectedModel.textInputSupport,
          imageInputSupport: selectedModel.imageInputSupport,
          audioInputSupport: selectedModel.audioInputSupport,
          textOutputSupport: selectedModel.textOutputSupport,
          imageOutputSupport: selectedModel.imageOutputSupport,
          audioOutputSupport: selectedModel.audioOutputSupport,          thinkingSupport: selectedModel.thinkingSupport,
          contextWindowTokens: selectedModel.contextWindowTokens || undefined,
          maxResponseTokens: selectedModel.maxResponseTokens || undefined,          knowledgeCutoffDate: selectedModel.knowledgeCutoffDate || undefined,
          lastCheckedDate: selectedModel.lastCheckedDate || undefined,
          supportedLanguages: selectedModel.supportedLanguages || undefined,
          warningDate: selectedModel.warningDate || undefined,
          warningMessage: selectedModel.warningMessage || undefined,
          deprecationDate: selectedModel.deprecationDate || undefined,
          deprecationMessage: selectedModel.deprecationMessage || undefined,
          defaultServiceGroup: selectedModel.defaultServiceGroup || undefined,
          defaultEnabled: selectedModel.defaultEnabled !== null ? selectedModel.defaultEnabled : undefined,
          displayOrder: selectedModel.displayOrder || undefined,
          recommendedUseCase: selectedModel.recommendedUseCase || undefined,
          performanceRating: selectedModel.performanceRating || undefined,
        };

        console.log("Create input:", createLLMModelInput);

        try {
          const response = await API.graphql({
            query: createLLMModel,
            variables: { input: createLLMModelInput },
          });
          console.log("Create successful:", response);
          setSuccess("Model created successfully");
          setOpenEditDialog(false);
          setIsCreatingNew(false);
          await fetchModels();
        } catch (graphqlError) {
          console.error("GraphQL Create Error:", graphqlError);
          
          if (graphqlError && typeof graphqlError === 'object' && 'errors' in graphqlError) {
            const errorMessages = (graphqlError as any).errors?.map((e: any) => e.message).join(', ');
            setError("GraphQL Error: " + errorMessages);
          } else {
            setError("Error creating model: " + String(graphqlError));
          }
          return;
        }
      } else {
        // Update existing model
        console.log("Creating update input:", selectedModel);
        console.log("Selected model status:", selectedModel.status);        const updateLLMModelInput: UpdateLLMModelInput = {
          id: selectedModel.id,
          status: selectedModel.status,
          vendor: selectedModel.vendor,
          host: selectedModel.host,
          supported: selectedModel.supported,
          name: selectedModel.name,
          description: selectedModel.description ?? null,
          modelKey: selectedModel.modelKey ?? undefined,
          modelKeyLatest: selectedModel.modelKeyLatest ?? undefined,
          inputTokenPrice: parseNumberOrUndefined(selectedModel.inputTokenPrice),
          cachedTokenPrice: parseNumberOrUndefined(selectedModel.cachedTokenPrice),
          outputTokenPrice: parseNumberOrUndefined(selectedModel.outputTokenPrice),
          inputTokenBatchPrice: parseNumberOrUndefined(selectedModel.inputTokenBatchPrice),          outputTokenBatchPrice: parseNumberOrUndefined(selectedModel.outputTokenBatchPrice),
          searchCost: parseNumberOrUndefined(selectedModel.searchCost),
          supportsSearch: selectedModel.supportsSearch,
          textInputSupport: selectedModel.textInputSupport,
          imageInputSupport: selectedModel.imageInputSupport,
          audioInputSupport: selectedModel.audioInputSupport,
          textOutputSupport: selectedModel.textOutputSupport,
          imageOutputSupport: selectedModel.imageOutputSupport,
          audioOutputSupport: selectedModel.audioOutputSupport,          thinkingSupport: selectedModel.thinkingSupport,
          contextWindowTokens: selectedModel.contextWindowTokens
            ? selectedModel.contextWindowTokens
            : undefined,
          maxResponseTokens: selectedModel.maxResponseTokens
            ? selectedModel.maxResponseTokens
            : undefined,          knowledgeCutoffDate: selectedModel.knowledgeCutoffDate
            ? selectedModel.knowledgeCutoffDate
            : undefined,
          lastCheckedDate: selectedModel.lastCheckedDate
            ? selectedModel.lastCheckedDate
            : undefined,
          supportedLanguages: selectedModel.supportedLanguages
            ? selectedModel.supportedLanguages
            : undefined,
          warningDate: selectedModel.warningDate
            ? selectedModel.warningDate
            : undefined,
          warningMessage: selectedModel.warningMessage
            ? selectedModel.warningMessage
            : undefined,
          deprecationDate: selectedModel.deprecationDate
            ? selectedModel.deprecationDate
            : undefined,
          deprecationMessage: selectedModel.deprecationMessage
            ? selectedModel.deprecationMessage
            : undefined,
          defaultServiceGroup: selectedModel.defaultServiceGroup
            ? selectedModel.defaultServiceGroup
            : undefined,defaultEnabled: selectedModel.defaultEnabled !== null
            ? selectedModel.defaultEnabled
            : undefined,
          displayOrder: selectedModel.displayOrder
            ? selectedModel.displayOrder
            : undefined,
          recommendedUseCase: selectedModel.recommendedUseCase
            ? selectedModel.recommendedUseCase
            : undefined,
          performanceRating: selectedModel.performanceRating
            ? selectedModel.performanceRating
            : undefined,
        };console.log("Update input:", updateLLMModelInput);
        console.log("Status being sent:", updateLLMModelInput.status);
        console.log("Calling API.graphql with mutation:", updateLLMModel);

        try {
          const response = await API.graphql({
            query: updateLLMModel,
            variables: { input: updateLLMModelInput },
          });
          console.log("Raw API Response:", response);

          if ("errors" in response) {
            throw new Error(JSON.stringify(response.errors));
          }

          console.log("Update successful:", response);
          setSuccess("Model updated successfully");
          setOpenEditDialog(false);
          await fetchModels();      } catch (graphqlError) {
          console.error("GraphQL Error:", graphqlError);
          
          // Extract more detailed error information
          if (graphqlError && typeof graphqlError === 'object' && 'errors' in graphqlError) {
            const errorMessages = (graphqlError as any).errors?.map((e: any) => e.message).join(', ');
            setError("GraphQL Error: " + errorMessages);
          } else {
            setError("Error updating model: " + String(graphqlError));
          }
          return; // Don't throw, just show the error
        }
      }
    } catch (err) {
      console.error("Save Error:", err);      setError("Error saving model: " + String(err));
    }
  };
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  const handleExportConfirm = () => {
    try {
      // Filter models based on selected statuses
      const selectedStatuses = Object.keys(exportStatusFilters).filter(status => exportStatusFilters[status]);
      const filteredModels = models.filter(model => selectedStatuses.includes(model.status));

      // Create export data with timestamp
      const exportData = {
        exportedAt: new Date().toISOString(),
        exportedBy: "AdminModels",
        version: "1.0",
        statusFilters: selectedStatuses,
        models: filteredModels
      };

      // Convert to JSON string with pretty formatting
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `llm-models-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      URL.revokeObjectURL(url);
      
      setSuccess(`Exported ${filteredModels.length} models successfully (filtered by status: ${selectedStatuses.join(', ')})`);
      setExportDialogOpen(false);
    } catch (err) {
      setError("Error exporting models: " + String(err));    }
  };

  const handleStatusFilterChange = (status: string, checked: boolean) => {
    setExportStatusFilters(prev => ({
      ...prev,
      [status]: checked
    }));
  };

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setImportData(content);
        setImportDialogOpen(true);
      } catch (err) {
        setError("Error reading file: " + String(err));
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const validateImportData = (data: any): ModelRecord[] => {
    if (!data || typeof data !== 'object') {
      throw new Error("Invalid JSON format");
    }

    let modelsToImport: any[];
    
    // Handle both direct array format and wrapped export format
    if (Array.isArray(data)) {
      modelsToImport = data;
    } else if (data.models && Array.isArray(data.models)) {
      modelsToImport = data.models;
    } else {
      throw new Error("Expected an array of models or export format with 'models' property");
    }

    // Validate each model has required fields
    const requiredFields = ['vendor', 'name', 'status'];
    const validatedModels: ModelRecord[] = [];

    for (let i = 0; i < modelsToImport.length; i++) {
      const model = modelsToImport[i];
      
      // Check required fields
      for (const field of requiredFields) {
        if (!model[field]) {
          throw new Error(`Model at index ${i} is missing required field: ${field}`);
        }
      }

      // Create validated model record with defaults for missing fields
      const validatedModel: ModelRecord = {
        id: model.id || '', // Will be generated by backend if empty
        status: model.status,
        vendor: model.vendor,
        host: model.host || '',
        supported: model.supported !== undefined ? model.supported : true,
        name: model.name,
        description: model.description || null,
        modelKey: model.modelKey || null,
        modelKeyLatest: model.modelKeyLatest || null,
        inputTokenPrice: model.inputTokenPrice || null,
        cachedTokenPrice: model.cachedTokenPrice || null,
        outputTokenPrice: model.outputTokenPrice || null,
        inputTokenBatchPrice: model.inputTokenBatchPrice || null,
        outputTokenBatchPrice: model.outputTokenBatchPrice || null,        searchCost: model.searchCost || null,
        supportsSearch: model.supportsSearch !== undefined ? model.supportsSearch : false,
        textInputSupport: model.textInputSupport !== undefined ? model.textInputSupport : true,
        imageInputSupport: model.imageInputSupport !== undefined ? model.imageInputSupport : false,
        audioInputSupport: model.audioInputSupport !== undefined ? model.audioInputSupport : false,
        textOutputSupport: model.textOutputSupport !== undefined ? model.textOutputSupport : true,
        imageOutputSupport: model.imageOutputSupport !== undefined ? model.imageOutputSupport : false,
        audioOutputSupport: model.audioOutputSupport !== undefined ? model.audioOutputSupport : false,
        thinkingSupport: model.thinkingSupport !== undefined ? model.thinkingSupport : false,
        contextWindowTokens: model.contextWindowTokens || null,
        maxResponseTokens: model.maxResponseTokens || null,        knowledgeCutoffDate: model.knowledgeCutoffDate || null,
        lastCheckedDate: model.lastCheckedDate || null,
        supportedLanguages: model.supportedLanguages || null,
        warningDate: model.warningDate || null,
        warningMessage: model.warningMessage || null,
        deprecationDate: model.deprecationDate || null,
        deprecationMessage: model.deprecationMessage || null,
        defaultServiceGroup: model.defaultServiceGroup || null,
        defaultEnabled: model.defaultEnabled || null,
        displayOrder: model.displayOrder || null,
        recommendedUseCase: model.recommendedUseCase || null,
        performanceRating: model.performanceRating || null,
      };

      validatedModels.push(validatedModel);
    }

    return validatedModels;
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      setError("No import data provided");
      return;
    }

    setImporting(true);
    try {
      // Parse JSON
      const parsedData = JSON.parse(importData);
      
      // Validate data structure
      const modelsToImport = validateImportData(parsedData);
      
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Import each model
      for (let i = 0; i < modelsToImport.length; i++) {
        const model = modelsToImport[i];
        
        try {
          const createInput: CreateLLMModelInput = {
            status: model.status,
            vendor: model.vendor,
            host: model.host,
            supported: model.supported,
            name: model.name,
            description: model.description || undefined,
            modelKey: model.modelKey || undefined,
            modelKeyLatest: model.modelKeyLatest || undefined,
            inputTokenPrice: model.inputTokenPrice || undefined,
            cachedTokenPrice: model.cachedTokenPrice || undefined,
            outputTokenPrice: model.outputTokenPrice || undefined,
            inputTokenBatchPrice: model.inputTokenBatchPrice || undefined,
            outputTokenBatchPrice: model.outputTokenBatchPrice || undefined,            searchCost: model.searchCost || undefined,
            supportsSearch: model.supportsSearch,
            textInputSupport: model.textInputSupport,
            imageInputSupport: model.imageInputSupport,
            audioInputSupport: model.audioInputSupport,
            textOutputSupport: model.textOutputSupport,
            imageOutputSupport: model.imageOutputSupport,
            audioOutputSupport: model.audioOutputSupport,
            thinkingSupport: model.thinkingSupport,
            contextWindowTokens: model.contextWindowTokens || undefined,
            maxResponseTokens: model.maxResponseTokens || undefined,            knowledgeCutoffDate: model.knowledgeCutoffDate || undefined,
            lastCheckedDate: model.lastCheckedDate || undefined,
            supportedLanguages: model.supportedLanguages || undefined,
            warningDate: model.warningDate || undefined,
            warningMessage: model.warningMessage || undefined,
            deprecationDate: model.deprecationDate || undefined,
            deprecationMessage: model.deprecationMessage || undefined,
            defaultServiceGroup: model.defaultServiceGroup || undefined,
            defaultEnabled: model.defaultEnabled !== null ? model.defaultEnabled : undefined,
            displayOrder: model.displayOrder || undefined,
            recommendedUseCase: model.recommendedUseCase || undefined,
            performanceRating: model.performanceRating || undefined,
          };

          await API.graphql({
            query: createLLMModel,
            variables: { input: createInput },
          });
          
          successCount++;
        } catch (err) {
          errorCount++;
          const errorMsg = `Model "${model.name}" (${model.vendor}): ${String(err)}`;
          errors.push(errorMsg);
          console.error(`Import error for model ${i}:`, err);
        }
      }

      // Show results
      if (successCount > 0) {
        setSuccess(`Successfully imported ${successCount} models${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
      }
      
      if (errorCount > 0) {
        setError(`Failed to import ${errorCount} models. First few errors: ${errors.slice(0, 3).join('; ')}`);
      }

      // Refresh models list
      await fetchModels();
      
      // Close dialog
      setImportDialogOpen(false);
      setImportData("");
      
    } catch (err) {
      setError("Error importing models: " + String(err));
    }
    
    setImporting(false);
  };
  
  const columns: GridColDef[] = [
    { field: "vendor", headerName: "Vendor", width: 100 },
    { field: "host", headerName: "Host", width: 100 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "name", headerName: "Name", width: 200 },
    {
      field: "supported",
      headerName: "Supported",
      width: 100,
      type: "boolean",
    },
    {
      field: "inputTokenPrice",
      headerName: "Input Price",
      width: 130,
      type: "number",
    },
    {
      field: "outputTokenPrice",
      headerName: "Output Price",
      width: 130,
      type: "number",
    },

    {
      field: "imageInputSupport",
      headerName: "Image In",
      width: 100,
      type: "boolean",
    },
    {
      field: "audioInputSupport",
      headerName: "Audio In",
      width: 100,
      type: "boolean",
    },
    {
      field: "textOutputSupport",
      headerName: "Text Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "imageOutputSupport",
      headerName: "Image Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "audioOutputSupport",
      headerName: "Audio Out",
      width: 100,
      type: "boolean",
    },    {
      field: "thinkingSupport",
      headerName: "Reasoning",
      width: 100,
      type: "boolean",
    },
    {
      field: "supportsSearch",
      headerName: "Search",
      width: 100,
      type: "boolean",
    },
    {
      field: "contextWindowTokens",
      headerName: "Context Tokens",
      width: 130,
      type: "number",
    },
    {
      field: "maxResponseTokens",
      headerName: "Max Tokens",
      width: 130,
      type: "number",
    },    {
      field: "knowledgeCutoffDate",
      headerName: "Knowledge Cutoff",
      width: 150,
    },
    {
      field: "warningDate",
      headerName: "Warning Date",
      width: 130,
    },
    {
      field: "warningMessage",
      headerName: "Warning Message",
      width: 200,
    },
    {
      field: "deprecationDate",
      headerName: "Deprecation Date",
      width: 140,
    },
    {
      field: "deprecationMessage",
      headerName: "Deprecation Message",
      width: 200,
    },
    {
      field: "defaultServiceGroup",
      headerName: "Default Group",
      width: 120,
    },
    {
      field: "defaultEnabled",
      headerName: "Default Enabled",
      width: 120,
      type: "boolean",
    },
    {
      field: "displayOrder",
      headerName: "Display Order",
      width: 110,
      type: "number",
    },
    {
      field: "performanceRating",
      headerName: "Performance",
      width: 110,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEditClick(params.row)}
        >
          Edit
        </Button>
      ),
    },  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Models Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}      <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddClick}
        >
          Add New Model
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleExport}
          disabled={models.length === 0}
        >
          Export Models ({models.length})
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          component="label"
        >
          Import Models
          <input
            type="file"
            hidden
            accept=".json"
            onChange={handleImportFile}
          />
        </Button>        <TextField
          label="Filter by Vendor"
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          size="small"
        />
      </Box>

      <Box sx={{ height: "100%", width: "100%" }}>
        <DataGrid
          rows={models.filter((model) =>
            model.vendor.toLowerCase().includes(filterVendor.toLowerCase())
          )}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          columns={columns}
          getRowHeight={() => "auto"}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: "outputTokenPrice", sort: "desc" }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
                vendor: true,
                host: false,
                supported: true,
                status: true,
                name: true,
                description: false,
                inputTokenPrice: true,
                outputTokenPrice: true,
                contextWindowTokens: true,
                maxResponseTokens: true,
                textInputSupport: false,
                imageInputSupport: false,
                audioInputSupport: false,
                textOutputSupport: false,
                imageOutputSupport: false,
                audioOutputSupport: false,
                thinkingSupport: false,                knowledgeCutoffDate: false,
                lastCheckedDate: true,
                supportedLanguages: false,
                warningDate: false,
                warningMessage: false,
                deprecationDate: false,
                deprecationMessage: false,
                defaultServiceGroup: true,
                defaultEnabled: true,
                displayOrder: false,
                performanceRating: true,
                recommendedUseCase: false,
              },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          //disableSelectionOnClick
          loading={loading}
          sx={{
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
        />
      </Box>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isCreatingNew ? 'Add New Model' : 'Edit Model'}</DialogTitle>
        <DialogContent>
          {selectedModel && (
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {/* Basic Info */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Vendor"
                  value={selectedModel.vendor}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      vendor: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Host"
                  value={selectedModel.host}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      host: e.target.value,
                    })
                  }
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={selectedModel.status}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        status: e.target.value,
                      })
                    }                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="research">Research</MenuItem>
                    <MenuItem value="beingDeprecated">
                      Being Deprecated
                    </MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Name"
                  value={selectedModel.name}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      name: e.target.value,
                    })
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.supported}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          supported: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Supported"
                />
                <TextField
                  size="small"
                  label="Model Key"
                  value={selectedModel.modelKey || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      modelKey: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Latest Model Key"
                  value={selectedModel.modelKeyLatest || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      modelKeyLatest: e.target.value,
                    })
                  }
                />
              </Box>

              {/* Pricing */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >                <TextField
                  size="small"
                  label="Input Token Price"
                  type="number"
                  value={selectedModel.inputTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      inputTokenPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Output Token Price"
                  type="number"
                  value={selectedModel.outputTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      outputTokenPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Cached Token Price"
                  type="number"
                  value={selectedModel.cachedTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      cachedTokenPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Input Batch Price"
                  type="number"
                  value={selectedModel.inputTokenBatchPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      inputTokenBatchPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Output Batch Price"
                  type="number"
                  value={selectedModel.outputTokenBatchPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      outputTokenBatchPrice: e.target.value,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Search Cost"
                  type="number"
                  value={selectedModel.searchCost || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      searchCost: e.target.value,
                    })
                  }
                />
              </Box>

              {/* Support Flags */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.textInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          textInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Text Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.imageInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          imageInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Image Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.audioInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          audioInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Audio Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.textOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          textOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Text Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.imageOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          imageOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Image Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.audioOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          audioOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Audio Output"
                />                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.thinkingSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          thinkingSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Reasoning"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.supportsSearch || false}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          supportsSearch: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Supports Search"
                />
              </Box>

              {/* Other Fields */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >                <TextField
                  size="small"
                  label="Context Window Tokens"
                  type="number"
                  value={selectedModel.contextWindowTokens || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      contextWindowTokens: parseInt(e.target.value) || undefined,
                    })
                  }
                />                <TextField
                  size="small"
                  label="Max Response Tokens"
                  type="number"
                  value={selectedModel.maxResponseTokens || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      maxResponseTokens: parseInt(e.target.value) || undefined,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Knowledge Cutoff Date"
                  value={selectedModel.knowledgeCutoffDate || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      knowledgeCutoffDate: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Last Checked Date"
                  value={selectedModel.lastCheckedDate || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      lastCheckedDate: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Description"
                  multiline
                  rows={4}
                  value={selectedModel.description || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      description: e.target.value,
                    })
                  }
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 2,
                }}
              >                <TextField
                  size="small"
                  label="Supported Languages"
                  multiline
                  rows={2}
                  value={selectedModel.supportedLanguages || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      supportedLanguages: e.target.value,
                    })
                  }
                />
              </Box>

              {/* New Service Management Fields */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Service Management Settings
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >
                <FormControl size="small" fullWidth>
                  <InputLabel>Default Service Group</InputLabel>
                  <Select
                    label="Default Service Group"
                    value={selectedModel.defaultServiceGroup || ""}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        defaultServiceGroup: e.target.value,
                      })
                    }                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Super Small">Super Small</MenuItem>
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Premium">Premium</MenuItem>
                    <MenuItem value="Reasoning">Reasoning</MenuItem>
                    <MenuItem value="Super Premium">Super Premium</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.defaultEnabled || false}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          defaultEnabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Default Enabled"
                />
                <TextField
                  size="small"
                  label="Display Order"
                  type="number"
                  value={selectedModel.displayOrder || ""}                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      displayOrder: parseInt(e.target.value) || undefined,
                    })
                  }
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Performance Rating</InputLabel>
                  <Select
                    label="Performance Rating"
                    value={selectedModel.performanceRating || ""}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        performanceRating: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Fast">Fast</MenuItem>
                    <MenuItem value="Balanced">Balanced</MenuItem>
                    <MenuItem value="Powerful">Powerful</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Recommended Use Case"
                  multiline
                  rows={2}
                  value={selectedModel.recommendedUseCase || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      recommendedUseCase: e.target.value,
                    })
                  }
                  helperText="Describe when users should choose this model"
                />
              </Box>
            </Box>
          )}
        </DialogContent>        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setIsCreatingNew(false);
          }}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            {isCreatingNew ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export LLM Models</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>            <Typography variant="body1">
              Select which model statuses to include in the export:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.keys(exportStatusFilters).sort().map(status => (
                <FormControlLabel
                  key={status}
                  control={
                    <Switch
                      checked={exportStatusFilters[status] || false}
                      onChange={(e) => handleStatusFilterChange(status, e.target.checked)}
                    />
                  }
                  label={`${status} (${models.filter(m => m.status === status).length} models)`}
                />
              ))}
            </Box>
            {Object.keys(exportStatusFilters).length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {Object.values(exportStatusFilters).filter(Boolean).length > 0 
                  ? `Will export ${models.filter(model => exportStatusFilters[model.status]).length} models`
                  : 'No models will be exported with current selection'
                }
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleExportConfirm}
            variant="contained"
            color="primary"
            disabled={Object.values(exportStatusFilters).filter(Boolean).length === 0}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import LLM Models</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1">
              Import models from a JSON file. Ensure the file is in the correct format.
            </Typography>
            <TextField
              label="Paste JSON Data"
              multiline
              rows={10}
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              variant="outlined"
              fullWidth
              helperText="Or use the file upload option below"
            />
            <Button
              variant="contained"
              component="label"
              size="small"
            >
              Upload File
              <input
                type="file"
                accept=".json"
                onChange={handleImportFile}
                hidden
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            color="primary"
            disabled={importing}
          >
            {importing ? <CircularProgress size={24} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
